/* ============================================================
   APC 2026 — KV background animation
   ============================================================
   This file has two halves:

   [1] SHOPIFY SHIM — page integration glue. Picks the canvas
       out of the DOM, defeats ancestor transforms, pauses the
       loop when the tab is hidden. Re-use this verbatim each
       time the background animation is re-ported from a local
       prototype.

   [2] ANIMATION BODY — verbatim port of the local alt10X
       prototype (currently alt102), with two intentional diffs:
         a) renderer attaches to an existing <canvas> instead of
            creating its own and appending to <body>.
         b) all HUD / phase / timecode DOM manipulation removed.
       To bump to a new alt: replace everything between the
       BEGIN/END ANIMATION BODY markers below.
   ============================================================ */

(function () {
  // ─── [1] SHOPIFY SHIM ──────────────────────────────────────
  // SP detection used by multiple init steps (Three.js downscaling,
  // Lenis skip, scroll-out pause). Single source of truth to keep
  // PC/SP behavior in sync.
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  function init() {
    const root = document.querySelector('.apc26-root');
    if (!root) return;
    const canvas = root.querySelector('.apc26-kv-canvas');
    if (!canvas) return;

    // Set `theme-color` so the mobile browser UI (iOS Safari
    // status bar area, Android Chrome address bar) matches the
    // page's true-black backdrop instead of defaulting to the
    // theme's gray. The meta tag is injected if not already in
    // <head> by the layout. Safari 15+ / Chrome (Android) 39+.
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeMeta);
    }
    themeMeta.setAttribute('content', '#000000');

    // Move canvas to the very front of <body> so:
    //  1. its `position: fixed` resolves to the viewport (theme
    //     wrappers often carry a transform/contain that would
    //     turn into the containing block);
    //  2. it sits FIRST in DOM order — both canvas (z-index: 0,
    //     stacking context via positioned+z-index) and
    //     `.apc26-root` (z-index: 0, stacking context via
    //     positioned+z-index) end up at body level. Same level
    //     → DOM order decides paint order, so we want canvas
    //     first to put content on top. `prepend` is idempotent;
    //     calling it when the canvas is already first is a noop.
    document.body.prepend(canvas);

    // Strip transform / filter / contain on every ancestor of
    // the page root. Overflow on those ancestors is also relaxed
    // so the canvas isn't clipped. The matching CSS handles the
    // `background: transparent` half.
    let cursor = root.parentElement;
    while (cursor && cursor !== document.documentElement) {
      const cs = getComputedStyle(cursor);
      if (cs.transform && cs.transform !== 'none') {
        cursor.style.setProperty('transform', 'none', 'important');
      }
      if (cs.filter && cs.filter !== 'none') {
        cursor.style.setProperty('filter', 'none', 'important');
      }
      if (cs.contain && cs.contain !== 'none' && cs.contain !== 'normal') {
        cursor.style.setProperty('contain', 'none', 'important');
      }
      if (cs.overflow && cs.overflow !== 'visible') {
        cursor.style.setProperty('overflow-y', 'visible', 'important');
      }
      cursor = cursor.parentElement;
    }

    if (typeof THREE === 'undefined') {
      console.error('[apc26] Three.js failed to load');
      return;
    }

    // Pause rAF when the tab is hidden OR (on SP) when the user
    // has scrolled past the hero region. On SP we additionally
    // fade out the canvas + mist (KV-only design) so the page
    // below the hero is pure black, freeing the compositor from
    // ongoing mist/blur work. Drop accumulated delta on resume so
    // the animation doesn't fast-forward by the pause duration.
    let isHidden = document.visibilityState === 'hidden';
    let isOutOfHero = false;
    let isPaused = isHidden;
    let pendingResume = false;
    function recomputePauseState() {
      const nowPaused = isHidden || isOutOfHero;
      if (isPaused && !nowPaused) pendingResume = true;
      isPaused = nowPaused;
    }
    document.addEventListener('visibilitychange', function () {
      isHidden = (document.visibilityState === 'hidden');
      recomputePauseState();
    });
    // SP only: KV-only behavior. IntersectionObserver on the hero
    // spacer (100vh) — once it leaves viewport, fade canvas+mist
    // and pause rAF. Cleaner trigger than scrollY threshold (no
    // interaction with iOS address-bar resize).
    if (isMobile) {
      const heroSpacer = root.querySelector('.apc26-hero-spacer');
      if (heroSpacer && typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver(function (entries) {
          const next = !entries[0].isIntersecting;
          if (next !== isOutOfHero) {
            isOutOfHero = next;
            recomputePauseState();
            document.body.classList.toggle('apc26-kv-hidden', next);
          }
        }, { threshold: 0 });
        io.observe(heroSpacer);
      }
    }

    // Mouse parallax: PC + non-touch + non-reduced-motion only.
    // Updated by the listener below; read by the animation loop
    // via hooks.mouseRef each frame.
    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mouseTarget = { x: 0, y: 0 };
    if (supportsHover && !reducedMotion) {
      window.addEventListener('mousemove', function (e) {
        mouseTarget.x = (e.clientX / window.innerWidth) - 0.5;
        mouseTarget.y = (e.clientY / window.innerHeight) - 0.5;
      }, { passive: true });
    }

    // Defer Three.js startup so the title CSS animation gets a
    // clean main thread. Shader compilation + geometry build can
    // block 100–500ms on a phone — running them after the title
    // load-in (~1s) makes the entrance feel reliable on 4G.
    const _hooks = {
      isPausedRef: () => isPaused,
      consumeResume: () => {
        const v = pendingResume;
        pendingResume = false;
        return v;
      },
      mouseTarget: mouseTarget,
    };
    const _startNow = function () { startAnimation(canvas, _hooks); };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(_startNow, { timeout: 1500 });
    } else {
      setTimeout(_startNow, 1200);
    }

    initCountdown();
    initSmoothScroll();
    initScrollReveal();
    initPinTransition(root);
  }

  // ─── Pin & Shrink transition ────────────────────────────────
  // Reads scroll progress (0 → 1 during the first viewport) and
  // writes the value into a CSS custom property on `.apc26-root`.
  // CSS interpolates everything else (title size, position, gap,
  // countdown size, mask opacity, etc.) via calc() on that var.
  function initPinTransition(root) {
    let ticking = false;
    const pinned = root.querySelector('.apc26-pinned');
    function update() {
      const vh = window.innerHeight || 800;
      const t = Math.min(1, Math.max(0, window.scrollY / vh));
      // Ease-out to soften the very end of the transition.
      const eased = 1 - Math.pow(1 - t, 1.6);
      root.style.setProperty('--apc26-pin-t', eased.toFixed(4));
      // Apply Figma compact layout when scroll nearly complete (smooth via CSS animation).
      const titleEl = root.querySelector('.apc26-title');
      if (titleEl) titleEl.classList.toggle('is-compact', eased > 0.72);
      // Release the fixed pinned layer when the root ends so the
      // footer can cover it. Once root.bottom rises above viewport
      // bottom, translate the pinned upward by the same delta.
      if (pinned) {
        const rect = root.getBoundingClientRect();
        // PC: pin content (countdown) is anchored to the viewport
        // bottom, so the natural anchor is `vh` — release fires the
        // moment the footer touches the bottom.
        // SP: pin content sits at the top (top:30). Use the deepest
        // child bottom as the anchor so the pin stays visible until
        // root.bottom actually crosses that content edge.
        let naturalBottom = vh;
        const isSP = window.innerWidth < 1080;
        if (isSP) {
          const currentRelease = parseFloat(
            getComputedStyle(root).getPropertyValue('--apc26-pin-release')
          ) || 0;
          naturalBottom = 0;
          const probes = pinned.querySelectorAll('.apc26-countdown, .apc26-title');
          for (let i = 0; i < probes.length; i++) {
            const b = probes[i].getBoundingClientRect().bottom + currentRelease;
            if (b > naturalBottom) naturalBottom = b;
          }
        }
        const release = Math.max(0, naturalBottom - rect.bottom);
        root.style.setProperty('--apc26-pin-release', release + 'px');
      }
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ─── Smooth scroll (Lenis) ─────────────────────────────────
  // Premium feel for the scroll motion itself. Desktop only —
  // mobile keeps the OS-native momentum scroll, which already
  // feels good and would conflict with Lenis on touch.
  function initSmoothScroll() {
    // SP: skip entirely. Lenis script is also not loaded on SP
    // (see page-apc-26.liquid) so the typeof check below would
    // bail anyway — this just makes the intent explicit.
    if (isMobile) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let lenis = null;
    if (typeof Lenis !== 'undefined' && !reducedMotion) {
      lenis = new Lenis({
        duration: 1.15,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
        smoothTouch: false,
        touchMultiplier: 2,
        wheelMultiplier: 1.0,
      });
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }

    // Nav anchor links — Lenis 経由でスムーズスクロール
    document.querySelectorAll('.apc26-nav a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        const id = a.getAttribute('href');
        if (!id || id === '#') return;
        const target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        if (lenis) {
          lenis.scrollTo(target, { offset: -80, duration: 1.2 });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        history.pushState(null, '', id);
      });
    });
  }

  // ─── Scroll-driven reveal ──────────────────────────────────
  // Adds `is-revealed` to any `[data-apc26-reveal]` element when
  // it enters the viewport. CSS handles the actual visual
  // transition. Each element fires once, then is unobserved.
  function initScrollReveal() {
    const targets = document.querySelectorAll('[data-apc26-reveal]');
    if (!targets.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: reveal everything immediately.
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  // ─── Countdown ─────────────────────────────────────────────
  // Tick once a second. Target date is the event start time.
  // Update this single literal when the event time is finalized.
  function initCountdown() {
    const target = new Date('2026-05-27T14:00:00+09:00').getTime();
    const els = {
      days:    document.querySelector('[data-countdown="days"]'),
      hours:   document.querySelector('[data-countdown="hours"]'),
      minutes: document.querySelector('[data-countdown="minutes"]'),
      seconds: document.querySelector('[data-countdown="seconds"]'),
    };
    if (!els.days) return;

    // Adds the `.is-tick` punch class to a number when its value
    // changes — gives the countdown a "living" feel. Class is
    // auto-removed on animationend (CSS keyframe).
    function setNum(el, padTo, value) {
      const next = String(value).padStart(padTo, '0');
      if (el.textContent === next) return;
      el.textContent = next;
      el.classList.remove('is-tick');
      // Force reflow so the animation restarts cleanly.
      void el.offsetWidth;
      el.classList.add('is-tick');
    }

    function clearTick(e) {
      if (e.target && e.target.classList) {
        e.target.classList.remove('is-tick');
      }
    }
    Object.keys(els).forEach(function (k) {
      els[k].addEventListener('animationend', clearTick);
    });

    function tick() {
      let diff = Math.max(0, target - Date.now());
      const d = Math.floor(diff / 86400000); diff -= d * 86400000;
      const h = Math.floor(diff / 3600000);  diff -= h * 3600000;
      const m = Math.floor(diff / 60000);    diff -= m * 60000;
      const s = Math.floor(diff / 1000);
      setNum(els.days,    2, d);
      setNum(els.hours,   2, h);
      setNum(els.minutes, 2, m);
      setNum(els.seconds, 2, s);
    }
    tick();
    setInterval(tick, 1000);
  }

  // ─── [2] ANIMATION BODY ────────────────────────────────────
  // BEGIN ANIMATION BODY — alt102 port
  function startAnimation(targetCanvas, hooks) {
    try {
      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

      const scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(0x000000, 0.010);

      const camera = new THREE.PerspectiveCamera(
        58,
        window.innerWidth / window.innerHeight,
        0.1,
        300
      );
      const renderer = new THREE.WebGLRenderer({
        canvas: targetCanvas,
        antialias: true,
        alpha: false,
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
      // KV-only design (SP fades canvas after hero), so we can
      // afford full DPR 2 cap on both PC and SP — the heavy render
      // only runs while the user is actively viewing the hero.
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 1);

      window.addEventListener('resize', function () {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      });

      const LOOP_DURATION = 25.0;
      const NUM_LINES = 7;
      const FILAMENTS_PER_LINE = 4;
      const SEGMENTS_PER_FILAMENT = isMobile ? 80 : 120;

      const LINE_COLORS = [
        new THREE.Color(1.00, 1.00, 1.00),
        new THREE.Color(0.82, 0.86, 0.94),
        new THREE.Color(0.94, 0.88, 0.78),
        new THREE.Color(0.96, 0.92, 0.84),
        new THREE.Color(0.78, 0.82, 0.90),
        new THREE.Color(0.92, 0.92, 0.92),
        new THREE.Color(0.58, 0.60, 0.64),
      ];

      const filamentVertexShader = `
        attribute float aLineId;
        attribute float aT;
        attribute float aFilamentSeed;
        attribute float aIntroDelay;
        attribute float aLayer;
        attribute float aThickness;

        uniform float uTime;
        uniform float uProgress;
        uniform float uLoopSeed;
        uniform vec3 uColors[7];

        varying float vT;
        varying float vFilamentSeed;
        varying vec3 vColor;
        varying float vThickness;
        varying float vUnityAmt;
        varying float vIntroWriteProgress;
        varying float vEndFadeAmount;
        varying float vLayer;
        varying float vDarknessFade;
        varying float vNearness;
        varying float vLineId;
        varying float vXFade;

        float ss(float a, float b, float x) {
          float t = clamp((x - a) / (b - a), 0.0, 1.0);
          return t * t * (3.0 - 2.0 * t);
        }
        float alignAmount(float p) { return ss(0.48, 0.72, p); }
        float unityAmount(float p) {
          float buildUp = ss(0.55, 0.74, p);
          float release = 1.0 - ss(0.78, 0.86, p);
          return buildUp * release;
        }
        float hash(float n) { return fract(sin(n) * 43758.5453123); }

        void lineParams(float lineId,
                        out vec3 S_initial,
                        out vec3 dir_initial,
                        out float length_initial) {
          float align = alignAmount(uProgress);
          float baseA  = radians(-12.0) + sin(uTime * 0.10 + uLoopSeed * 0.13) * radians(2.5);
          float gap    = radians(90.0)  + sin(uTime * 0.07 + uLoopSeed * 0.21) * radians(4.0);
          baseA = mix(baseA, 0.0, align);
          gap   = mix(gap,   0.0, align);
          float baseB = baseA + gap;

          bool inA = (lineId < 2.5);
          float theta;
          vec2  pPerp;
          float midOffset;

          if (inA) {
            if (lineId < 0.5) {        theta = baseA + radians(-1.5); pPerp = vec2(0.0, 0.6); midOffset =  30.0; }
            else if (lineId < 1.5) {   theta = baseA + radians( 3.0); pPerp = vec2(0.0,-1.4); midOffset = -38.0; }
            else {                     theta = baseA + radians(-4.5); pPerp = vec2(0.0, 2.2); midOffset =  42.0; }
          } else {
            if (lineId < 3.5) {        theta = baseB + radians(-5.0); pPerp = vec2(-2.4, 0.0); midOffset = -34.0; }
            else if (lineId < 4.5) {   theta = baseB + radians(-1.5); pPerp = vec2(-0.6, 0.0); midOffset =  28.0; }
            else if (lineId < 5.5) {   theta = baseB + radians( 2.0); pPerp = vec2( 1.2, 0.0); midOffset = -40.0; }
            else {                     theta = baseB + radians( 5.5); pPerp = vec2( 2.6, 0.0); midOffset =  24.0; }
          }

          vec3 dir = vec3(cos(theta), sin(theta), 0.0);
          vec3 perpDir = vec3(-sin(theta), cos(theta), 0.0);

          float effectiveMid = midOffset * (1.0 - align);
          vec3 center  = perpDir * (pPerp.x + pPerp.y) + dir * effectiveMid;

          length_initial = 180.0;
          S_initial = center - dir * (length_initial * 0.5);
          dir_initial = dir;

          float j1 = hash(lineId * 17.0 + uLoopSeed + 1.0) - 0.5;
          float j2 = hash(lineId * 17.0 + uLoopSeed + 2.0) - 0.5;
          S_initial.x += j1 * 1.0;
          S_initial.y += j2 * 1.0;

          float idleAmp = (1.0 - alignAmount(uProgress)) * (1.0 - ss(0.92, 1.0, uProgress));
          float idlePhase = lineId * 1.73 + uLoopSeed * 0.31;
          vec3 idleDrift = vec3(
            sin(uTime * 0.23 + idlePhase) * 0.22,
            cos(uTime * 0.19 + idlePhase + 1.3) * 0.14,
            0.0
          ) * idleAmp;
          S_initial += idleDrift;
        }

        void main() {
          vT = aT;
          vLineId = aLineId;
          vFilamentSeed = aFilamentSeed;
          vThickness = aThickness;
          vLayer = aLayer;

          float align = alignAmount(uProgress);
          float unity = unityAmount(uProgress);
          vUnityAmt = unity;

          float introWindow = 0.40;
          float writeStart = aIntroDelay * (introWindow - 0.10);
          float writeDuration = 0.12;
          float writeProgress = clamp((uProgress - writeStart) / writeDuration, 0.0, 1.0);
          float outroProgress = ss(0.86, 1.0, uProgress);
          vIntroWriteProgress = writeProgress * (1.0 - outroProgress);

          vEndFadeAmount = smoothstep(0.0, 0.5, aT) * smoothstep(1.0, 0.5, aT);
          vDarknessFade = 1.0 - ss(0.96, 1.0, uProgress);

          vec3 S_initial, dir_initial;
          float length_initial;
          lineParams(aLineId, S_initial, dir_initial, length_initial);
          vec3 E_initial = S_initial + dir_initial * length_initial;

          vec3 unifyCenter = vec3(0.0, 1.2, 0.0);
          vec3 unifyDir    = vec3(1.0, 0.0, 0.0);
          float unifyHalf  = 70.0;
          vec3 S_unified = unifyCenter - unifyDir * unifyHalf;
          vec3 E_unified = unifyCenter + unifyDir * unifyHalf;

          vec3 S_now = mix(S_initial, S_unified, align);
          vec3 E_now = mix(E_initial, E_unified, align);

          vec3 corePos = mix(S_now, E_now, aT);
          vec3 lineDir = normalize(E_now - S_now);

          float distFromCam = length(corePos - cameraPosition);
          float nearness = 1.0 - smoothstep(3.0, 35.0, distFromCam);
          vNearness = nearness;

          vec3 viewDir = normalize(corePos - cameraPosition);
          vec3 rightScreen = cross(lineDir, viewDir);
          float rightLen = length(rightScreen);
          rightScreen = (rightLen > 0.0001) ? rightScreen / rightLen : vec3(0.0, 1.0, 0.0);

          float motionDamp = 1.0 - vUnityAmt * 0.95;
          float sign01 = hash(aFilamentSeed + 1.0);
          float fOffset = (sign01 - 0.5) * 2.0;
          float spreadBase = mix(0.04, 0.10, hash(aFilamentSeed + 2.0));
          float spreadAmt = spreadBase * nearness;
          vec3 perpOffset = rightScreen * (fOffset * spreadAmt);

          float wobblePhase = hash(aFilamentSeed + 3.0) * 6.2831853;
          float jitterMag = mix(0.004, 0.014, hash(aFilamentSeed + 4.0)) * motionDamp * nearness;
          vec3 wobble = rightScreen * sin(aT * 4.0 + wobblePhase + uTime * 0.35) * jitterMag;
          float axialWobble = sin(aT * 18.0 + wobblePhase + uTime * 1.2) * 0.008 * nearness;
          wobble += lineDir * axialWobble * motionDamp;

          vec3 worldPos = corePos + perpOffset + wobble;

          int li = int(aLineId + 0.5);
          vec3 lineCol;
          if      (li == 0) lineCol = uColors[0];
          else if (li == 1) lineCol = uColors[1];
          else if (li == 2) lineCol = uColors[2];
          else if (li == 3) lineCol = uColors[3];
          else if (li == 4) lineCol = uColors[4];
          else if (li == 5) lineCol = uColors[5];
          else              lineCol = uColors[6];

          float tintRoll = hash(aFilamentSeed + 60.0);
          float retainAmt = smoothstep(0.45, 1.0, tintRoll) * 0.25;
          vec3 unityTint = mix(vec3(1.0), lineCol, retainAmt);
          float warmth = (hash(aFilamentSeed + 61.0) - 0.5) * 0.08;
          unityTint += vec3(warmth, warmth * 0.4, -warmth);
          unityTint = clamp(unityTint, vec3(0.0), vec3(1.0));
          vColor = mix(lineCol, unityTint, unity);

          float lateFade = ss(0.82, 0.98, uProgress);
          float fadeThreshold = mix(120.0, -120.0, lateFade);
          vXFade = 1.0 - smoothstep(fadeThreshold - 4.0, fadeThreshold + 8.0, worldPos.x);

          gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
        }
      `;

      const filamentFragmentShader = `
        varying float vT;
        varying float vFilamentSeed;
        varying vec3 vColor;
        varying float vThickness;
        varying float vUnityAmt;
        varying float vIntroWriteProgress;
        varying float vEndFadeAmount;
        varying float vLayer;
        varying float vDarknessFade;
        varying float vNearness;
        varying float vLineId;
        varying float vXFade;

        uniform float uTime;
        uniform float uProgress;

        float hash(float n) { return fract(sin(n) * 43758.5453123); }
        float lengthNoise(float t, float seed, float scale) {
          float a = floor(t * scale);
          float b = a + 1.0;
          float fa = hash(seed + a * 13.7);
          float fb = hash(seed + b * 13.7);
          float f = fract(t * scale);
          f = f * f * (3.0 - 2.0 * f);
          return mix(fa, fb, f);
        }

        void main() {
          float introMask = smoothstep(vIntroWriteProgress, vIntroWriteProgress - 0.1, vT);
          if (introMask <= 0.001) discard;

          float endFade = pow(vEndFadeAmount, 2.6);

          float macro = lengthNoise(vT + uTime * 0.04, vFilamentSeed * 0.7, 3.0);
          float meso  = lengthNoise(vT + uTime * 0.10, vFilamentSeed * 1.3, 8.0);
          float micro = lengthNoise(vT + uTime * 0.30, vFilamentSeed * 2.1, 22.0);
          float brightnessTexture = macro * 0.55 + meso * 0.30 + micro * 0.15;
          brightnessTexture = pow(brightnessTexture, 1.15);

          float pulseSpeed = 0.6 + hash(vFilamentSeed + 30.0) * 1.4;
          float pulseFreq  = 4.0 + hash(vFilamentSeed + 31.0) * 4.0;
          float pulse = sin(vT * pulseFreq - uTime * pulseSpeed + vFilamentSeed * 0.7) * 0.5 + 0.5;
          brightnessTexture *= mix(0.85, 1.20, pulse);

          float flickerFreq = 6.0 + hash(vFilamentSeed + 40.0) * 10.0;
          float flicker = lengthNoise(uTime * flickerFreq * 0.07, vFilamentSeed + 41.0, 1.0);
          brightnessTexture *= mix(0.88, 1.10, flicker);

          float unityTexTarget = mix(0.65, 1.10, hash(vFilamentSeed + 12.0));
          brightnessTexture = mix(brightnessTexture, unityTexTarget, vUnityAmt);

          float intrinsic = 0.40 + 0.50 * hash(vFilamentSeed + 10.0);
          float unityIntrinsic = mix(0.45, 1.00, hash(vFilamentSeed + 11.0));
          intrinsic = mix(intrinsic, unityIntrinsic, vUnityAmt);

          float thicknessBoost = 0.7 + vThickness * 0.7;
          float brightness = intrinsic * brightnessTexture * thicknessBoost;

          float tempGrad = sin(vT * 3.14159 + vFilamentSeed * 2.3) * 0.5 + 0.5;
          vec3 warmCol = vColor + vec3( 0.05,  0.02, -0.05);
          vec3 coolCol = vColor + vec3(-0.04, -0.01,  0.05);
          vec3 baseLineCol = mix(warmCol, coolCol, tempGrad);

          float specRoll = hash(vFilamentSeed + 80.0);
          float specularBoost = 0.0;
          if (specRoll > 0.55) {
            float specSpeed = 0.4 + hash(vFilamentSeed + 81.0) * 1.2;
            float specPos   = fract(uTime * specSpeed * 0.10 + hash(vFilamentSeed + 82.0));
            float specWidth = 0.035 + hash(vFilamentSeed + 83.0) * 0.04;
            float d = vT - specPos;
            specularBoost = exp(-(d * d) / (specWidth * specWidth)) * (specRoll - 0.55) / 0.45;
          }

          vec3 col;
          if (vLayer > 0.5) {
            float haloShift = (hash(vFilamentSeed + 70.0) - 0.5) * 0.07;
            vec3 haloCol = baseLineCol + vec3(haloShift, haloShift * 0.3, -haloShift);
            col = haloCol * brightness * endFade * 0.60;
            col += vec3(specularBoost * 0.30) * endFade;
          } else {
            col = baseLineCol * brightness * endFade * 1.20;
            float hotSeed = hash(vFilamentSeed + 20.0);
            if (hotSeed > 0.75) {
              float hot = pow(macro, 6.0) * (hotSeed - 0.75) / 0.25 * 0.55;
              col += vec3(hot * 1.05, hot, hot * 0.95) * endFade;
            }
            col += vec3(specularBoost * 0.85) * endFade;
          }

          col *= 1.0 + vUnityAmt * 1.0;
          col *= introMask;
          col *= vDarknessFade;
          col *= vNearness;
          col *= vXFade;

          gl_FragColor = vec4(col, 1.0);
        }
      `;

      const LAYERS = 2;
      const totalFilaments = NUM_LINES * FILAMENTS_PER_LINE;
      const verticesPerFilamentPerLayer = SEGMENTS_PER_FILAMENT * 2;
      const totalVertices = totalFilaments * LAYERS * verticesPerFilamentPerLayer;

      const positions = new Float32Array(totalVertices * 3);
      const lineIds = new Float32Array(totalVertices);
      const tValues = new Float32Array(totalVertices);
      const seeds = new Float32Array(totalVertices);
      const thicknesses = new Float32Array(totalVertices);
      const introDelays = new Float32Array(totalVertices);
      const layers = new Float32Array(totalVertices);

      function lineThicknessBias(lineId) {
        if (lineId === 0) return 1.15;
        if (lineId === 5) return 1.05;
        if (lineId === 2) return 0.80;
        if (lineId === 6) return 0.75;
        return 1.00;
      }

      let vi = 0;
      for (let l = 0; l < NUM_LINES; l++) {
        const lineStart = l * 0.05;
        const lineSpread = 0.20;
        const biasMul = lineThicknessBias(l);
        for (let f = 0; f < FILAMENTS_PER_LINE; f++) {
          const filamentSeed = l * 1000 + f * 7.31 + 0.5;
          const isHero = Math.random() < 0.22;
          const baseT = isHero ? 0.8 + Math.random() * 0.2 : 0.3 + Math.random() * 0.35;
          const thickness = baseT * biasMul;
          const withinLine = Math.random();
          const introDelay = Math.min(0.99, lineStart + withinLine * lineSpread);
          for (let layer = 0; layer < LAYERS; layer++) {
            for (let s = 0; s < SEGMENTS_PER_FILAMENT; s++) {
              const t0 = s / SEGMENTS_PER_FILAMENT;
              const t1 = (s + 1) / SEGMENTS_PER_FILAMENT;
              lineIds[vi] = l; tValues[vi] = t0; seeds[vi] = filamentSeed; thicknesses[vi] = thickness; introDelays[vi] = introDelay; layers[vi] = layer; vi++;
              lineIds[vi] = l; tValues[vi] = t1; seeds[vi] = filamentSeed; thicknesses[vi] = thickness; introDelays[vi] = introDelay; layers[vi] = layer; vi++;
            }
          }
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('aLineId', new THREE.BufferAttribute(lineIds, 1));
      geometry.setAttribute('aT', new THREE.BufferAttribute(tValues, 1));
      geometry.setAttribute('aFilamentSeed', new THREE.BufferAttribute(seeds, 1));
      geometry.setAttribute('aThickness', new THREE.BufferAttribute(thicknesses, 1));
      geometry.setAttribute('aIntroDelay', new THREE.BufferAttribute(introDelays, 1));
      geometry.setAttribute('aLayer', new THREE.BufferAttribute(layers, 1));

      const filamentMaterial = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uProgress: { value: 0 },
          uLoopSeed: { value: 0 },
          uColors: { value: LINE_COLORS },
        },
        vertexShader: filamentVertexShader,
        fragmentShader: filamentFragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const filamentMesh = new THREE.LineSegments(geometry, filamentMaterial);
      scene.add(filamentMesh);

      const COMMON_OPEN_POS  = new THREE.Vector3( 5.5, 10.0, 13.5);
      const COMMON_OPEN_LOOK = new THREE.Vector3( 0.0,  0.0,  0.0);

      const CAMERA_ROUTES = [
        [
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
          { pos: new THREE.Vector3( 4.0,  8.0, 10.0),             look: new THREE.Vector3(-0.3,  0.0,  -0.5) },
          { pos: new THREE.Vector3( 1.0,  5.5,  7.0),             look: new THREE.Vector3(-0.4,  0.3,  -1.0) },
          { pos: new THREE.Vector3(-0.5,  3.8,  5.5),             look: new THREE.Vector3(-0.5,  0.6,  -1.2) },
          { pos: new THREE.Vector3( 1.0,  2.7,  6.0),             look: new THREE.Vector3( 1.5,  0.8,  -0.6) },
          { pos: new THREE.Vector3( 7.0,  2.8,  5.5),             look: new THREE.Vector3(-7.0,  1.2,   0.0) },
          { pos: new THREE.Vector3( 7.0,  2.7, 11.0),             look: new THREE.Vector3(-1.5,  1.1,   0.0) },
          { pos: new THREE.Vector3( 3.0,  2.7, 16.0),             look: new THREE.Vector3( 0.0,  1.1,   0.0) },
          { pos: new THREE.Vector3( 0.0,  3.0, 25.0),             look: new THREE.Vector3( 0.0,  1.2,   0.0) },
          { pos: new THREE.Vector3(-0.5,  3.0, 25.0),             look: new THREE.Vector3( 0.2,  1.2,   0.0) },
          { pos: new THREE.Vector3(-1.2,  3.0, 25.0),             look: new THREE.Vector3(-0.4,  1.2,   0.0) },
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
        ],
        [
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
          { pos: new THREE.Vector3( 4.0,  8.0, 10.0),             look: new THREE.Vector3( 0.3,  0.0,  -0.5) },
          { pos: new THREE.Vector3(-1.0,  5.5,  7.0),             look: new THREE.Vector3( 0.4,  0.3,  -1.0) },
          { pos: new THREE.Vector3( 0.5,  3.8,  5.5),             look: new THREE.Vector3( 0.5,  0.6,  -1.2) },
          { pos: new THREE.Vector3(-1.0,  2.7,  6.0),             look: new THREE.Vector3(-1.5,  0.8,  -0.6) },
          { pos: new THREE.Vector3(-7.0,  2.8,  5.5),             look: new THREE.Vector3( 7.0,  1.2,   0.0) },
          { pos: new THREE.Vector3(-7.0,  2.7, 11.0),             look: new THREE.Vector3( 1.5,  1.1,   0.0) },
          { pos: new THREE.Vector3(-3.0,  2.7, 16.0),             look: new THREE.Vector3( 0.0,  1.1,   0.0) },
          { pos: new THREE.Vector3( 0.0,  3.0, 25.0),             look: new THREE.Vector3( 0.0,  1.2,   0.0) },
          { pos: new THREE.Vector3(-0.5,  3.0, 25.0),             look: new THREE.Vector3( 0.2,  1.2,   0.0) },
          { pos: new THREE.Vector3(-1.2,  3.0, 25.0),             look: new THREE.Vector3(-0.4,  1.2,   0.0) },
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
        ],
        [
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
          { pos: new THREE.Vector3( 5.5,  7.5, 10.5),             look: new THREE.Vector3( 0.3,  0.0,  -0.5) },
          { pos: new THREE.Vector3( 5.5,  5.5,  7.5),             look: new THREE.Vector3( 0.7,  0.2,  -1.0) },
          { pos: new THREE.Vector3( 5.0,  4.0,  6.0),             look: new THREE.Vector3( 1.0,  0.5,  -1.0) },
          { pos: new THREE.Vector3( 3.5,  3.0,  6.5),             look: new THREE.Vector3( 2.5,  0.8,  -0.5) },
          { pos: new THREE.Vector3( 7.5,  2.8,  5.5),             look: new THREE.Vector3(-7.0,  1.2,   0.0) },
          { pos: new THREE.Vector3( 7.0,  2.7, 11.0),             look: new THREE.Vector3(-1.5,  1.1,   0.0) },
          { pos: new THREE.Vector3( 3.0,  2.7, 16.0),             look: new THREE.Vector3( 0.0,  1.1,   0.0) },
          { pos: new THREE.Vector3( 0.0,  3.0, 25.0),             look: new THREE.Vector3( 0.0,  1.2,   0.0) },
          { pos: new THREE.Vector3(-0.5,  3.0, 25.0),             look: new THREE.Vector3( 0.2,  1.2,   0.0) },
          { pos: new THREE.Vector3(-1.2,  3.0, 25.0),             look: new THREE.Vector3(-0.4,  1.2,   0.0) },
          { pos: COMMON_OPEN_POS.clone(),                         look: COMMON_OPEN_LOOK.clone() },
        ],
      ];

      const INTRO_MODES = [
        [0.00, 0.16, 0.32, 0.48, 0.64, 0.80, 0.96],
        [0.96, 0.80, 0.64, 0.48, 0.32, 0.16, 0.00],
        [0.00, 0.12, 0.24, 0.50, 0.62, 0.74, 0.86],
        [0.55, 0.68, 0.82, 0.00, 0.12, 0.24, 0.36],
        [0.00, 0.32, 0.64, 0.16, 0.48, 0.80, 0.96],
        [0.00, 0.04, 0.08, 0.02, 0.05, 0.09, 0.06],
      ];

      function applyIntroPattern(baseOrders) {
        const vertsPerFilament = LAYERS * SEGMENTS_PER_FILAMENT * 2;
        let vi = 0;
        for (let l = 0; l < NUM_LINES; l++) {
          const baseOrder = baseOrders[l];
          for (let f = 0; f < FILAMENTS_PER_LINE; f++) {
            const withinLine = Math.random() * 0.12;
            const delay = Math.min(0.95, baseOrder * 0.85 + withinLine);
            for (let v = 0; v < vertsPerFilament; v++) {
              introDelays[vi++] = delay;
            }
          }
        }
        geometry.attributes.aIntroDelay.needsUpdate = true;
      }

      function jitterVec3(rangeX, rangeY, rangeZ) {
        return new THREE.Vector3(
          (Math.random() - 0.5) * rangeX,
          (Math.random() - 0.5) * rangeY,
          (Math.random() - 0.5) * rangeZ
        );
      }

      let cameraPosCurve  = null;
      let cameraLookCurve = null;
      let currentRouteIdx = 0;
      let currentIntroIdx = 0;
      let pendingLoopScale = 1.0;

      function rebuildCurvesForRoute(routeIdx) {
        const base = CAMERA_ROUTES[routeIdx];
        const N = base.length;
        const isSeamGuarded = (i) => (i === 0 || i === N - 1);
        const jPos = base.map((w, i) =>
          isSeamGuarded(i) ? w.pos.clone() : w.pos.clone().add(jitterVec3(0.9, 0.45, 0.7))
        );
        const jLook = base.map((w, i) =>
          isSeamGuarded(i) ? w.look.clone() : w.look.clone().add(jitterVec3(0.35, 0.22, 0.28))
        );
        cameraPosCurve  = new THREE.CatmullRomCurve3(jPos,  true, 'catmullrom', 0.4);
        cameraLookCurve = new THREE.CatmullRomCurve3(jLook, true, 'catmullrom', 0.4);
      }

      function pickNextLoopParams() {
        currentRouteIdx = Math.floor(Math.random() * CAMERA_ROUTES.length);
        currentIntroIdx = Math.floor(Math.random() * INTRO_MODES.length);
        applyIntroPattern(INTRO_MODES[currentIntroIdx]);
        filamentMaterial.uniforms.uLoopSeed.value = Math.random() * 1000.0;
        pendingLoopScale = 0.94 + Math.random() * 0.12;
        rebuildCurvesForRoute(currentRouteIdx);
      }
      pickNextLoopParams();

      function fovAt(progress) {
        if (progress < 0.74) {
          const t = progress / 0.74;
          const eased = t * t * (3 - 2 * t);
          return 62 - (62 - 60) * eased;
        } else {
          const t = (progress - 0.74) / 0.26;
          const eased = t * t * (3 - 2 * t);
          return 60 + (65 - 60) * eased;
        }
      }

      const clock = new THREE.Clock();
      let elapsed = 0;
      let currentLoopDuration = LOOP_DURATION * pendingLoopScale;

      // Lerped mouse offset for camera parallax. The shim updates
      // `hooks.mouseTarget` on every mousemove; we ease toward it
      // each frame for buttery smooth following (no jitter from
      // event rate, no jump on hard cursor moves).
      const mouseLerp = { x: 0, y: 0 };

      // SP: cap to 30fps. The camera moves over 25s/loop so 30fps
      // looks identical to 60fps to the eye; halves total GPU work.
      const MIN_FRAME_INTERVAL = isMobile ? 1000 / 30 : 0;
      let lastFrameTime = 0;
      function animate(now) {
        requestAnimationFrame(animate);
        if (hooks.isPausedRef()) return;
        if (MIN_FRAME_INTERVAL > 0) {
          const t = now || 0;
          if ((t - lastFrameTime) < MIN_FRAME_INTERVAL) return;
          lastFrameTime = t;
        }
        if (hooks.consumeResume()) {
          // Discard the giant accumulated delta from the pause.
          clock.getDelta();
        }
        try {
          const delta = clock.getDelta();
          elapsed += delta;
          if (elapsed >= currentLoopDuration) {
            elapsed -= currentLoopDuration;
            if (elapsed >= currentLoopDuration) elapsed = 0;
            pickNextLoopParams();
            currentLoopDuration = LOOP_DURATION * pendingLoopScale;
          }
          const progress = elapsed / currentLoopDuration;
          const time = elapsed * 0.5;

          filamentMaterial.uniforms.uTime.value = time;
          filamentMaterial.uniforms.uProgress.value = progress;

          const camPos  = cameraPosCurve.getPoint(progress);
          const camLook = cameraLookCurve.getPoint(progress);
          camPos.x += Math.sin(time * 0.3) * 0.04;
          camPos.y += Math.cos(time * 0.4) * 0.025;
          camPos.z += Math.sin(time * 0.35) * 0.03;

          // Mouse parallax — only takes effect on devices that
          // populate `hooks.mouseTarget` (i.e. hover + fine
          // pointer, non-reduced-motion). Touch devices keep the
          // target at (0,0) so the lerp stays at zero. The lerp
          // factor 0.04 means the camera takes ~25 frames (~0.4s
          // at 60fps) to fully reach the target.
          const mt = hooks.mouseTarget || { x: 0, y: 0 };
          mouseLerp.x += (mt.x - mouseLerp.x) * 0.04;
          mouseLerp.y += (mt.y - mouseLerp.y) * 0.04;
          camPos.x += mouseLerp.x * 1.4;
          camPos.y -= mouseLerp.y * 0.8;

          camera.position.copy(camPos);
          camera.lookAt(camLook);
          camera.fov = fovAt(progress);
          camera.updateProjectionMatrix();

          renderer.render(scene, camera);
        } catch (e) {
          console.error('[apc26] animate error:', e);
        }
      }
      animate();
    } catch (e) {
      console.error('[apc26] setup error:', e);
    }
  }
  // END ANIMATION BODY

  // ─── Boot ──────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
